import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import {
  api,
  type ReportScope,
  type TicketListDimension,
} from "../../../lib/api";
import { DASHBOARD_STALE_MS } from "../../../lib/dashboard-refresh";
import { queryKeys } from "../../../lib/queryKeys";
import { Overlay } from "../../ui/Overlay";
import { cn } from "../../ui/cn";
import type { RangeValue } from "../RangeFilter";

/**
 * A count on a report that opens into the tickets behind it.
 *
 * Every figure on this dashboard answers "how many", and the next question is
 * always "which ones". Until now the only way to find out was to rebuild the
 * filter by hand in the queue and hope it matched -- and when the two numbers
 * disagreed there was no way to tell which was wrong. That is the gap this
 * closes, and it is why the server repeats each report's own grouping rather
 * than accepting a filter: the list has to be the same predicate as the count,
 * asked for rows.
 *
 * A dialog rather than a link out to the queue. Clicking a number is a glance,
 * not a departure -- somebody reading a dashboard wants to see which tickets
 * those were and carry on reading, and a navigation loses the page they were
 * building an opinion about. The ticket links inside it are the departure, and
 * those are ordinary links.
 *
 * A zero renders as plain text, deliberately: a control that opens an empty
 * list teaches people the control is not worth pressing.
 */
export function TicketCount({
  count,
  dimension,
  value,
  label,
  range,
  scope,
}: {
  count: number;
  dimension: TicketListDimension;
  /** The report's own grouping key -- a pipeline or agent id, or nothing. */
  value?: string;
  /** What the dialog is about, in the reader's words: "Late", "Quote". */
  label: string;
  range: RangeValue;
  scope: ReportScope;
}) {
  const [open, setOpen] = useState(false);

  if (count === 0) return <span>{count.toLocaleString()}</span>;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={`Show the ${label.toLowerCase()} tickets`}
        className={cn(
          "tabular-nums underline decoration-dotted underline-offset-2",
          "hover:text-brand-600 dark:hover:text-brand-400",
        )}
      >
        {count.toLocaleString()}
      </button>

      {open ? (
        <TicketDialog
          dimension={dimension}
          value={value}
          label={label}
          range={range}
          scope={scope}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

function TicketDialog({
  dimension,
  value,
  label,
  range,
  scope,
  onClose,
}: {
  dimension: TicketListDimension;
  value?: string;
  label: string;
  range: RangeValue;
  scope: ReportScope;
  onClose: () => void;
}) {
  const list = useQuery({
    queryKey: queryKeys.dashboardReport("tickets", range, scope, [
      dimension,
      value,
    ]),
    queryFn: () => api.dashboardTickets(dimension, value, range, scope),
    staleTime: DASHBOARD_STALE_MS,
  });

  const tickets = list.data?.tickets ?? [];

  return (
    /* `text-left` because the trigger lives in a right-aligned table cell and
       the dialog would otherwise inherit it -- every subject in the list was
       shunted to the right of its own row. */
    <Overlay onClose={onClose}>
      <div className="text-left">
        <h2 className="mb-0.5 pr-8 text-sm font-semibold text-content">
          {label}
        </h2>
        <p className="mb-3 text-[11px] text-faint">
          The tickets behind that figure, newest first.
        </p>

        {list.isLoading ? (
          <p className="py-6 text-center text-xs text-faint">Loading…</p>
        ) : tickets.length === 0 ? (
          <p className="py-6 text-center text-xs text-faint">
            Nothing to show.
          </p>
        ) : (
          <ul className="max-h-[60vh] overflow-y-auto">
            {tickets.map((ticket) => (
              <li
                key={ticket.id}
                /* Banded, like every other list in Pulse. */
                className="flex items-baseline gap-2 px-1 py-1 text-xs odd:bg-surface even:bg-app"
              >
                <Link
                  to={`/v/open/${ticket.id}`}
                  className="shrink-0 text-brand-600 hover:underline dark:text-brand-400"
                >
                  #{ticket.id}
                </Link>
                <span className="min-w-0 flex-1 truncate text-content">
                  {ticket.subject}
                </span>
                <span className="shrink-0 text-[11px] text-faint">
                  {ticket.pipelineName ?? "No pipeline"}
                </span>
                <span className="hidden shrink-0 text-[11px] text-muted sm:inline">
                  {ticket.assigneeName ?? "Unassigned"}
                </span>
              </li>
            ))}
          </ul>
        )}

        {/* Said rather than hidden, for the reason the closed report gives about
          a silently capped population: a truncated list that looks complete is
          worse than one that admits what it left out. */}
        {list.data?.truncated ? (
          <p className="mt-2 text-[11px] text-faint">
            Only the 50 most recent are listed.
          </p>
        ) : null}
      </div>
    </Overlay>
  );
}
